/**
 * Catalog Service
 * API calls for Catalog page data
 */

import { apiClient } from "@/lib/api-client"
import type {
  CatalogUser,
  Expert,
  HighlightedExpert,
  HighlightedRitualist,
  Intention,
} from "../types/catalog.types"

interface PaginatedResponse<T> {
  success: boolean
  data: {
    experts?: T[]
    users?: T[]
    pagination: {
      page: number
      limit: number
      totalItems: number
      totalPages: number
      hasNextPage: boolean
    }
  }
  message: string
}

interface ListResponse<T> {
  success: boolean
  data: T[]
  message: string
}

interface CatalogParams {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  intentions?: string[]
  spiritualTraditions?: string[]
  city?: string
  state?: string
  country?: string
}

function buildQueryParams(params?: CatalogParams): string {
  if (!params) return ""

  const queryParams = new URLSearchParams()
  if (params.page) queryParams.append("page", params.page.toString())
  if (params.limit) queryParams.append("limit", params.limit.toString())
  if (params.search) queryParams.append("search", params.search)
  if (params.sortBy) queryParams.append("sort_by", params.sortBy)
  if (params.intentions?.length) {
    queryParams.append("intentions", params.intentions.join(","))
  }
  if (params.spiritualTraditions?.length) {
    queryParams.append("spiritual_traditions", params.spiritualTraditions.join(","))
  }
  if (params.city) queryParams.append("city", params.city)
  if (params.state) queryParams.append("state", params.state)
  if (params.country) queryParams.append("country", params.country)

  const queryString = queryParams.toString()
  return queryString ? `?${queryString}` : ""
}

/**
 * Get experts for the Experts catalog
 */
export async function getExperts(
  params?: CatalogParams,
): Promise<PaginatedResponse<Expert>["data"]> {
  const url = `/api/connect/experts${buildQueryParams(params)}`
  const response = await apiClient.get<PaginatedResponse<Expert>>(url)
  return response.data.data
}

/**
 * Get ritualists for the Ritualists catalog
 */
export async function getRitualists(
  params?: CatalogParams,
): Promise<PaginatedResponse<CatalogUser>["data"]> {
  const url = `/api/connect/ritualists${buildQueryParams(params)}`
  const response = await apiClient.get<PaginatedResponse<CatalogUser>>(url)
  return response.data.data
}

/**
 * Get highlighted ritualists
 */
export async function getHighlightedRitualists(limit?: number): Promise<HighlightedRitualist[]> {
  const url = limit
    ? `/api/connect/highlighted-ritualists?limit=${limit}`
    : "/api/connect/highlighted-ritualists"

  const response = await apiClient.get<ListResponse<HighlightedRitualist>>(url)
  return response.data.data
}

/**
 * Get highlighted experts (availableForCoaching, sorted by follower count)
 */
export async function getHighlightedExperts(limit?: number): Promise<HighlightedExpert[]> {
  const url = limit
    ? `/api/connect/highlighted-experts?limit=${limit}`
    : "/api/connect/highlighted-experts"

  const response = await apiClient.get<ListResponse<HighlightedExpert>>(url)
  return response.data.data
}

/**
 * Get members for the Members catalog
 */
export async function getMembers(
  params?: CatalogParams,
): Promise<PaginatedResponse<CatalogUser>["data"]> {
  const url = `/api/connect/members${buildQueryParams(params)}`
  const response = await apiClient.get<PaginatedResponse<CatalogUser>>(url)
  return response.data.data
}

/**
 * Get intentions list for the Intentions catalog
 */
export async function getIntentions(userId?: string): Promise<Intention[]> {
  const response = await apiClient.get<{
    success: boolean
    data: {
      intentions: Array<{
        id: string
        title: string
        description: string
        is_checked: boolean
      }>
      total: number
    }
    message: string
  }>("/api/profiles/intentions", {
    params: { page: 1, limit: 100, ...(userId ? { user_id: userId } : {}) },
  })

  return response.data.data.intentions.map(i => ({
    id: i.id,
    name: i.title,
    isChecked: i.is_checked,
  }))
}
